import { useQuery } from '@tanstack/react-query';
import {
  hrUploadIntentSchema,
  hrUploadStatusSchema,
  isScanTerminal,
  signaturePageInputSchema,
  signaturePageResultSchema,
  type HrUploadPurpose,
  type HrUploadStatus,
  type SignaturePageInput,
  type SignaturePageResult,
} from '@vigeo/shared';
import { fixtureMode } from './data-mode.js';

/**
 * HR's uploads into quarantine (#188, ADR-009 §2): direct-record evidence and PDF template sources.
 *
 * The browser PUTs straight to quarantine with a write-only single-blob SAS, exactly as the portal
 * does; the API then measures, sniffs, seals and hashes what landed. The file is USABLE only once
 * its scan is `clean`, so callers poll `useUploadStatus` and hold the save or publish until then —
 * the server refuses anything else regardless.
 */

/** Where an upload is reserved: a person's record evidence, or an agency template source. */
export type UploadTarget =
  | {
      readonly kind: 'evidence';
      readonly personId: string;
      readonly recordId?: string;
      /**
       * What the file is for. The server stores it on the reservation and each consumer refuses an
       * upload staged for the other, so a health document cannot be filed as an offer letter.
       * Omitted means record evidence, which is what every caller but the offer letter wants.
       */
      readonly purpose?: HrUploadPurpose;
    }
  | { readonly kind: 'template-source' };

function base(target: UploadTarget): string {
  return target.kind === 'evidence' ? `/api/people/${encodeURIComponent(target.personId)}/uploads` : '/api/catalog/template-sources';
}

async function errorFrom(response: Response, fallback: string): Promise<Error> {
  const body = await response.json().catch(() => null) as { error?: unknown } | null;
  return new Error(typeof body?.error === 'string' ? body.error : fallback);
}

async function postJson(path: string, payload: unknown): Promise<unknown> {
  const response = await fetch(path, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(payload) });
  if (!response.ok) throw await errorFrom(response, 'That upload did not work. Please try again.');
  return response.json();
}

export interface StagedUpload {
  readonly uploadId: string;
  /** Evidence: the record every upload for it shares, sent with the save. */
  readonly recordId?: string;
  readonly status: HrUploadStatus;
}

/** Reserve, PUT the bytes to quarantine, and confirm them. Returns before any scan verdict. */
export async function stageUpload(target: UploadTarget, file: File): Promise<StagedUpload> {
  if (fixtureMode) throw new Error('Uploading is unavailable in the design preview.');
  const intent = hrUploadIntentSchema.parse(await postJson(base(target), {
    contentType: file.type,
    byteLength: file.size,
    fileName: file.name,
    ...(target.kind === 'evidence' && target.recordId !== undefined ? { recordId: target.recordId } : {}),
    ...(target.kind === 'evidence' && target.purpose !== undefined ? { purpose: target.purpose } : {}),
  }));
  // Required by the Blob REST API for a single-shot PUT, and allowed by the account's CORS rule.
  const put = await fetch(intent.uploadUrl, { method: 'PUT', headers: { 'x-ms-blob-type': 'BlockBlob', 'content-type': file.type }, body: file });
  // Never the storage account's own error: it is XML and names the account.
  if (!put.ok) throw new Error('The upload did not finish. Please try again.');
  const status = hrUploadStatusSchema.parse(await postJson(`${base(target)}/${encodeURIComponent(intent.uploadId)}/complete`, {}));
  return { uploadId: intent.uploadId, ...(intent.recordId === undefined ? {} : { recordId: intent.recordId }), status };
}

export async function fetchUploadStatus(target: UploadTarget, uploadId: string): Promise<HrUploadStatus> {
  const response = await fetch(`${base(target)}/${encodeURIComponent(uploadId)}`);
  if (!response.ok) throw await errorFrom(response, 'The upload could not be checked.');
  return hrUploadStatusSchema.parse(await response.json());
}

/** Polls an upload until its scan reaches a verdict. Idle when there is no upload. */
export function useUploadStatus(target: UploadTarget, uploadId: string | null) {
  return useQuery({
    queryKey: ['hr-upload', target.kind, target.kind === 'evidence' ? target.personId : null, uploadId],
    queryFn: () => fetchUploadStatus(target, uploadId!),
    enabled: uploadId !== null && !fixtureMode,
    refetchInterval: (query) => {
      const data = query.state.data;
      // `scan_failed` is final for this upload too: only a new upload is scanned again.
      return data !== undefined && (data.state !== 'accepted' || isScanTerminal(data.scan) || data.scan === 'scan_failed') ? false : 4000;
    },
  });
}

/** Append the reviewed acknowledgment page. The composite comes back as a NEW upload to be scanned. */
export async function appendTemplateSignaturePage(uploadId: string, input: SignaturePageInput): Promise<SignaturePageResult> {
  if (fixtureMode) throw new Error('Uploading is unavailable in the design preview.');
  return signaturePageResultSchema.parse(await postJson(`/api/catalog/template-sources/${encodeURIComponent(uploadId)}/signature-page`, signaturePageInputSchema.parse(input)));
}
